import type { MotionPreference, StoredSettings } from '../ui';

export const SAVE_KEY = 'lux-relay-save-v1';

export interface SaveData {
  version: 1;
  bestScore: number;
  totalRelays: number;
  totalPerfects: number;
  totalBankShots: number;
  bestCombo: number;
  runs: number;
  settings: StoredSettings;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const DEFAULT_SAVE: SaveData = {
  version: 1,
  bestScore: 0,
  totalRelays: 0,
  totalPerfects: 0,
  totalBankShots: 0,
  bestCombo: 0,
  runs: 0,
  settings: { muted: false, motion: 'auto' },
};

function finiteNonNegative(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
}

function motionPreference(value: unknown): MotionPreference {
  return value === 'full' || value === 'reduced' || value === 'auto' ? value : 'auto';
}

export function sanitizeSave(value: unknown): SaveData {
  if (!value || typeof value !== 'object') return structuredClone(DEFAULT_SAVE);
  const source = value as Partial<SaveData> & { settings?: Partial<StoredSettings> };
  return {
    version: 1,
    bestScore: finiteNonNegative(source.bestScore, 0),
    totalRelays: finiteNonNegative(source.totalRelays, 0),
    totalPerfects: finiteNonNegative(source.totalPerfects, 0),
    totalBankShots: finiteNonNegative(source.totalBankShots, 0),
    bestCombo: finiteNonNegative(source.bestCombo, 0),
    runs: finiteNonNegative(source.runs, 0),
    settings: {
      muted: typeof source.settings?.muted === 'boolean' ? source.settings.muted : false,
      motion: motionPreference(source.settings?.motion),
    },
  };
}

export function loadSave(storage?: StorageLike): SaveData {
  if (!storage) return structuredClone(DEFAULT_SAVE);
  try {
    const raw = storage.getItem(SAVE_KEY);
    return raw ? sanitizeSave(JSON.parse(raw) as unknown) : structuredClone(DEFAULT_SAVE);
  } catch {
    return structuredClone(DEFAULT_SAVE);
  }
}

export function persistSave(save: SaveData, storage?: StorageLike): void {
  if (!storage) return;
  try {
    storage.setItem(SAVE_KEY, JSON.stringify(sanitizeSave(save)));
  } catch {
    // Private browsing or exhausted storage must never block gameplay.
  }
}

export function updateRunSave(
  save: SaveData,
  result: { score: number; relays: number; perfects: number; bankShots: number; bestCombo: number },
): SaveData {
  return {
    ...save,
    bestScore: Math.max(save.bestScore, Math.floor(result.score)),
    totalRelays: save.totalRelays + Math.max(0, Math.floor(result.relays)),
    totalPerfects: save.totalPerfects + Math.max(0, Math.floor(result.perfects)),
    totalBankShots: save.totalBankShots + Math.max(0, Math.floor(result.bankShots)),
    bestCombo: Math.max(save.bestCombo, Math.floor(result.bestCombo)),
    runs: save.runs + 1,
  };
}

